
import { fontText, hexToRgbA, StylesIndexInjections } from 'common-react';
import { fieldBoxShadow, flexCenter, focusStyle, scrollbarStyleMixin } from '../../styles/mixins';
import { makeUiStyles, Theme, createStyles } from '../../styles/styles';
import { DropdownMultiSize } from './tools/DropdownMulti.const';

export interface CustomDropdownStyleProps {
  isDarkTheme?: boolean;
  value: string | string[];
  placeholder?: string;
  multiple?: boolean;
  variant?: 'default' | 'compact';
  size?: DropdownMultiSize;
}

const getPlaceholderColor = (theme: Theme, value: string | string[], isDarkTheme = false, multiple = false) => {
  if (isDarkTheme && multiple) {
    return value.length > 0 ? theme.color.greys[80] : theme.color.greys[20]
  }
  if (!multiple) {
    return isDarkTheme ? theme.color.greys[20] : theme.color.greys[50]
  }
  return value.length > 0 ? theme.color.greys[80] : theme.color.greys[50]
}

const dropdownSize = (size?: DropdownMultiSize) => {
  if (size === DropdownMultiSize.SMALL) {
    return {
      padding: '10px 32px 10px 10px',
      height: 40,
    }
  }
  return {
    padding: '16px 48px 16px 16px',
    height: 56,
  }
}

export default makeUiStyles((theme: Theme) => createStyles({
  formControl: {
    '& .MuiInput-formControl': {
      margin: 0,
    },
    '& .MuiFormLabel-root': {
      marginBottom: 8
    }
  },
  menuItemLabelWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  menuItemLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  root: {
    color: ({ value, isDarkTheme, multiple }: CustomDropdownStyleProps) => getPlaceholderColor(theme, value, isDarkTheme, multiple),
    font: fontText('normal', theme.fontSize.base, 24),
    border: `1px solid ${theme.color.greys[40]}`,
    height: props => dropdownSize(props.size).height,
    backgroundColor: theme.color.greys[0],
    borderRadius: 8,
    padding: 0,
    [theme.breakpoints.down('xs')]: {
      overflow: 'hidden',
      width: '100%',
      display: 'flex',
      justifyContent: 'center'
    },
    '&.Mui-error': {
      position: 'relative',
      borderColor: 'transparent',
      outline: `2px solid ${theme.color.reds[100]}`,
      boxShadow: fieldBoxShadow(theme).bs1,
    },
    '&.Mui-focused': {
      color: ({ value, isDarkTheme, multiple }: CustomDropdownStyleProps) => getPlaceholderColor(theme, value, isDarkTheme, multiple),
      backgroundColor: theme.color.greys[0],
      boxShadow: fieldBoxShadow(theme).bs1,
      outline: 'none',
      borderColor: theme.color.greys[80],
    },
    '&.Mui-focusVisible': {
      ...focusStyle(theme),
      boxShadow: 'none',
      borderColor: theme.color.greys[40]
    },
    '&:hover': {
      boxShadow: fieldBoxShadow(theme).bs1,
      outline: 'none',
      borderColor: theme.color.greys[80],
    },
    '&.Mui-disabled': {
      border: `1px solid ${hexToRgbA(theme.color.greys[40], 0.4)}`,
      outline: 'none',
      boxShadow: 'none',
      color: hexToRgbA(theme.color.greys[40], 0.4),
      backgroundColor: theme.color.greys[0],
      pointerEvents: 'none',
      '& .MuiSelect-root': {
        color: hexToRgbA(theme.color.greys[40], 0.4),
      }
    },
    '& .MuiSelect-root': {
      padding: props => dropdownSize(props.size).padding,
      color: theme.color.greys[80],
      font: fontText('normal', theme.fontSize.base, 24),
      backgroundColor: 'transparent',
      '&:focus': {
        backgroundColor: 'transparent',
      }
    },
    '& .MuiSelect-icon': {
      '&.Mui-disabled': {
        color: hexToRgbA(theme.color.oranges[50], 0.4),
      }
    },
    '& $select': {
      minWidth: 140,
      font: fontText('normal', theme.fontSize.base, 24),
      [theme.breakpoints.down('xs')]: {
        minWidth: 'auto',
      },
      '&:focus': {
        backgroundColor: theme.color.greys[0],
      },
    },
  },
  icon: {
    position: 'absolute',
    right: 16,
    transform: 'rotate(0deg)',
    top: 'calc(50% - 10px)',
    width: 20,
    height: 20,
    pointerEvents: 'none',
    willChange: 'transform',
    transition: 'transform 0.2s ease-in-out',
    '&.MuiSelect-iconOpen': {
      transform: 'rotate(180deg)',
    },
  },
  menuItem: {
    color: theme.color.greys[80],
    font: fontText('normal', theme.fontSize.base, 24),
    whiteSpace: 'unset',
    padding: 16,
    '&:hover': {
      backgroundColor: theme.color.greys[20],
    },
    '&.Mui-selected': {
      backgroundColor: theme.color.greys[20],
    },
    '&.Mui-focusVisible': {
      ...focusStyle(theme, -2)
    }
  },
  placeholder: {
    color: theme.color.greys[50],
    font: fontText('normal', theme.fontSize.base, 24),
  },
  selected: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: theme.color.greys[80],
    font: fontText('normal', theme.fontSize.base, 24),
  },
  popover: {
    border: `1px solid ${theme.color.greys[15]}`,
    marginTop: 8,
    borderRadius: 8,
    backgroundColor: theme.color.greys[0],
    overflow: 'hidden',
    boxShadow: fieldBoxShadow(theme).bs3,
    '& .MuiList-root': {
      'overflow-y': 'auto',
      maxHeight: 418,
      ...scrollbarStyleMixin({ theme }),
    },
    '& .MuiList-padding': {
      padding: '16px 4px 0 4px',
    },
    '& .MuiMenuItem-root': {
      paddingTop: 0,
      paddingBottom: 0
    }
  },
  menuItemMulti: {
    marginBottom: 4,
    '&:hover': {
      backgroundColor: theme.color.greys[15],
    },
    '&:focus-visible': {
      ...focusStyle(theme, -2),
      borderRadius: 8,
    },
    '&.Mui-selected': {
      backgroundColor: `transparent`,
      '&:hover': {
        backgroundColor: theme.color.greys[10],
      },
    },
  },
  menuItemTextMulti: {
    marginRight: 20,
    whiteSpace: 'break-spaces',
    minWidth: 140
  },
  actionButtonBox: {
    ...flexCenter(),
    position: 'sticky',
    padding: '16px 12px',
    bottom: 0,
    borderTop: `1px solid ${theme.color.greys[15]}`,
    backgroundColor: theme.color.greys[0],
  },
  numberOfOptions: {
    marginLeft: 2,
    opacity: 1,
    transition: 'opacity 0.2s ease-in-out',
  },
  menuItemContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '16px 0',
  },
  numberOfOptionsEmpty: {
    opacity: 0,
  },
  renderValue: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'space-between',
  }
}), { name: 'DropdownMulti', index: StylesIndexInjections.UI_MOLECULES });
